import React, { useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Link as RouterLink } from "react-router-dom";
import { requestTciSharing } from "services/fde/fdeService";

export default function TciSharingControl({ tci, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  if (!tci || tci.scope !== "institutional") {
    return null;
  }

  const sharingStatus = tci.sharingStatus || "none";
  const isPending = sharingStatus === "pending-review";
  const isApproved = sharingStatus === "approved";
  const isRejected = sharingStatus === "rejected";
  const checked = Boolean(tci.sharedWithGlobal) || isPending || isApproved;

  const handleToggle = async (event) => {
    const nextShare = event.target.checked;
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await requestTciSharing({ tciId: tci.id, share: nextShare });
      setSuccessMessage(
        nextShare
          ? "Sharing request submitted for platform review."
          : "Sharing request withdrawn.",
      );
      if (onUpdated) await onUpdated();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to update sharing preference.");
    } finally {
      setSaving(false);
    }
  };

  const checkbox = (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={handleToggle}
          disabled={saving || isApproved}
          inputProps={{
            "aria-label": "Share this TCI with the global platform catalog",
          }}
        />
      }
      label="Share this TCI with the global platform catalog"
    />
  );

  return (
    <Box sx={{ mt: 2 }}>
      {errorMessage ? (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      ) : null}
      {successMessage ? (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      ) : null}

      {isPending ? (
        <Tooltip title="Pending platform review. Uncheck to withdraw the request.">
          <Box>{checkbox}</Box>
        </Tooltip>
      ) : (
        checkbox
      )}

      {isPending ? (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
          Pending platform review
        </Typography>
      ) : null}

      {isApproved && tci.promotedGlobalTciId ? (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
          Shared globally as{" "}
          <Typography
            component={RouterLink}
            to={`/dashboard/global-tci-manager?highlight=${encodeURIComponent(tci.promotedGlobalTciId)}`}
            variant="body2"
            color="primary"
          >
            {tci.promotedGlobalTciId}
          </Typography>
        </Typography>
      ) : null}

      {isRejected ? (
        <Typography variant="body2" color="error" sx={{ ml: 4 }}>
          Rejected: {tci.sharingRejectionReason || "No reason provided."}
        </Typography>
      ) : null}

      {saving ? (
        <LoadingButton loading size="small" sx={{ visibility: "hidden" }}>
          Saving
        </LoadingButton>
      ) : null}
    </Box>
  );
}
